import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import {
    AbstractControl,
    AbstractControlOptions,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';

import * as RecentPoliciesActions from '../store/recent-policies/recent-policies.actions';

import { RoutePageComponent } from '../shared/route-page/route-page.component';
import { PSIC_COMPONENTS, Field, FieldOptions } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { RecaptchaModule } from 'ng-recaptcha';
import { ThirdPartyLoginHeaderComponent } from '../third-party-login-header/third-party-login-header.component';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { PolicyDetailsService } from '../services/policy-details.service';
import { Store } from '@ngrx/store';
import { loadPolicyDetailsSuccess } from '../store/policy/policy.actions';
import { LoadingService } from '../shared/loading.service';
import { ToastService } from '../shared/toast/toast.service';
import { LoadingComponent } from '../shared/loading/loading.component';
import { ToastComponent } from '../shared/toast/toast.component';
import { MatIconModule } from '@angular/material/icon';
import { Location } from '@angular/common';
import { TokenService } from '../services/token.service';
import { SessionService } from '../services/session.service';
import { EmailService } from '../services/email.service';
import { CreateTaskIrisService, IrisTaskPayload } from '../services/create-task-iris.service';
import { SelectionService } from '../services/selection.service';
import { Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppConfig, APP_CONFIG } from 'src/app.config';
import * as PolicyActions from '../store/policy/policy.actions';
import { Subject, takeUntil } from 'rxjs';
import { addRecentPolicy } from '../store/recent-policies/recent-policies.actions';

export interface LoginAttempt {
    policyNumber: string;
    loanNumber: string;
    zipCode: string;
    insuredType: string;
    firstName: string;
    lastName: string;
    trustName: string;
}

@Component({
    selector: 'app-third-party-login',
    imports: [
        ReactiveFormsModule,
        RoutePageComponent,
        ...PSIC_COMPONENTS,
        RecaptchaModule,
        ThirdPartyLoginHeaderComponent,
        LoadingComponent,
        ToastComponent,
        MatIconModule,
    ],
    templateUrl: './third-party-login.component.html',
    styleUrl: './third-party-login.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class ThirdPartyLoginComponent implements OnInit, OnDestroy {
    recaptchaValid = false;
    sessionId: string | null = null;
    isThirdParty = true;
    isLoading = false;
    radioLeft = true;
    failedAttempts = 0;
    showContactModal = false;

    form: FormGroup;
    contactForm: FormGroup;
    failedAttemptLog: LoginAttempt[] = [];
    // for third-party login failed attempts
    failedAttemptToken: string | undefined = undefined;
    private destroy$ = new Subject<void>();

    policyNumberField = new Field({
        name: 'policyNumber',
        placeholder: 'Policy number',
        type: 'text',
        caption: 'Policy Number',
        helpIcon: true,
        required: true,
    });
    loanNumberField = new Field({
        name: 'loanNumber',
        placeholder: 'Loan number',
        type: 'text',
        caption: '',
        helpIcon: false,
        required: false,
    });
    zipCodeField = new Field({
        name: 'zipCode',
        placeholder: 'Zip Code',
        type: 'text',
        caption: '',
        helpIcon: false,
        required: false,
        maxLength: 5,
        minLength: 5,
    });
    firstNameField = new Field({
        name: 'firstName',
        placeholder: 'First name',
        type: 'text',
        caption: '',
        helpIcon: false,
        required: false,
    });
    lastNameField = new Field({
        name: 'lastName',
        placeholder: 'Last name',
        type: 'text',
        caption: '',
        helpIcon: false,
        required: false,
    });
    trustNameField = new Field({
        name: 'trustName',
        placeholder: 'Trust / LLC name',
        type: 'text',
        caption: '',
        helpIcon: false,
        required: false,
    });
    insuredTypeField = new Field({
        name: 'insuredType',
        value: '',
        caption: '',
        type: 'radio',
        required: false,
        description: '',
        options: {
            optionsList: [
                new Field({
                    name: 'insuredType',
                    value: 'insured',
                    caption: '',
                    description: 'Insured',
                    type: 'radio',
                    required: false,
                    id: 'insured',
                    action: 'navigate',
                }),
                new Field({
                    name: 'insuredType',
                    value: 'trust',
                    caption: '',
                    description: 'Trust / LLC',
                    type: 'radio',
                    required: false,
                    id: 'trust',
                    action: 'navigate',
                }),
            ],
        },
    });

    contactEmailField = new Field({
        name: 'contactEmail',
        placeholder: 'Email',
        type: 'text',
        caption: 'Email',
        required: true,
    });
    contactPhoneField = new Field({
        name: 'contactPhone',
        placeholder: 'Phone',
        type: 'text',
        caption: 'Phone',
        required: true,
    });
    contactDescriptionField = new Field({
        name: 'contactDescription',
        placeholder: 'Type here...',
        type: 'text',
        caption: 'Describe the issue',
        required: true,
        maxLength: 300,
        description: '(Including your full name, policy number, and your interest in the policy).',
    });

    formOptions: AbstractControlOptions = {
        validators: this.authValidator(),
    };

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private policyDetailsService: PolicyDetailsService,
        private store: Store,
        private loadingService: LoadingService,
        private toastService: ToastService,
        private location: Location,
        private tokenService: TokenService,
        private sessionService: SessionService,
        private emailService: EmailService,
        private createTaskIrisService: CreateTaskIrisService,
        private selectionService: SelectionService,
        private http: HttpClient,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {
        this.form = this.fb.group(
            {
                policyNumber: [''],
                loanNumber: [''],
                zipCode: ['', [Validators.minLength(5), Validators.maxLength(5)]], // ← update this line
                insuredType: [null],
                firstName: [''],
                lastName: [''],
                trustName: [''],
            },
            this.formOptions
        );

        this.contactForm = this.fb.group({
            contactEmail: ['', [Validators.required, Validators.email]],
            contactPhone: [
                '',
                [Validators.required, Validators.pattern('^\\(?([0-9]{3})\\)?[-.\\s]?([0-9]{3})[-.\\s]?([0-9]{4})$')],
            ],
            contactDescription: ['', [Validators.required, Validators.maxLength(300)]],
        });
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    authValidator(): ValidatorFn {
        return (form: AbstractControl): ValidationErrors | null => {
            const loan = form.get('loanNumber')?.value?.trim();
            const zip = form.get('zipCode')?.value?.trim();
            const type = form.get('insuredType')?.value;
            const trustName = form.get('trustName')?.value?.trim();
            const firstName = form.get('firstName')?.value?.trim();
            const lastName = form.get('lastName')?.value?.trim();

            let count = 0;

            // Count loan number as 1 field
            if (loan) count++;

            // Count zip code as 1 field
            if (zip) count++;

            // Count name as 1 field if properly filled
            if (type === 'insured') {
                // Only count if BOTH first and last name are filled
                if (firstName && lastName) {
                    count++;
                }
            } else if (type === 'trust') {
                // Only count if trust name is filled
                if (trustName) {
                    count++;
                }
            }
            // If no radio selected or fields incomplete, name doesn't count (but doesn't invalidate either)

            // Need at least 2 out of 3: loan, zip, name
            return count >= 2 ? null : { invalidAuth: true };
        };
    }
    get insuredType() {
        return this.form.get('insuredType')?.value;
    }
    onRadioClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const clickedValue = target.closest('input[type="radio"]')
            ? (target.closest('input[type="radio"]') as HTMLInputElement).value
            : null;

        if (clickedValue && this.form.get('insuredType')?.value === clickedValue) {
            this.form.patchValue({ insuredType: null, firstName: '', lastName: '', trustName: '' });
        }
    }

    ngOnInit() {
        this.sessionId = this.route.snapshot.queryParamMap.get('sessionId');
        const policyNumber = this.route.snapshot.queryParamMap.get('policyNumber');

        // Persist the TP session so orchestrator calls can attach it as a header.
        this.sessionService.setSessionId(this.sessionId);

        if (!this.sessionId) {
            this.toastService.add({
                type: 'error',
                title: '',
                message: 'Please access the page through the proper link.',
            });
        }

        if (policyNumber) {
            this.form.patchValue({ policyNumber });
        }

        const showSuccess = this.route.snapshot.queryParamMap.get('showSuccess');
        if (showSuccess) {
            this.toastService.add({
                type: 'success',
                title: '',
                message:
                    'Your message was sent successfully. We will contact you shortly to help you resolve this issue.',
            });
            this.location.replaceState(`/third-party-login?sessionId=${this.sessionId}`);
        }
        this.contactForm.get('contactEmail')?.valueChanges.subscribe((val) => {
            this.contactEmailField = new Field({
                ...this.contactEmailField,
                caption: val ? 'Email' : '',
            });
        });
        this.contactForm.get('contactPhone')?.valueChanges.subscribe((val) => {
            this.contactPhoneField = new Field({
                ...this.contactPhoneField,
                caption: val ? 'Phone' : '',
            });
        });

        this.contactForm.get('contactDescription')?.valueChanges.subscribe((val) => {
            this.contactDescriptionField = new Field({
                ...this.contactDescriptionField,
                caption: val ? 'Describe the issue' : '',
            });
        });
        // this.form.get('policyNumber')?.valueChanges.subscribe(val => {
        //     this.policyNumberField = new Field({
        //         ...this.policyNumberField,
        //         caption: val ? 'Policy Number' : ''
        //     });
        // });

        this.form.get('loanNumber')?.valueChanges.subscribe((val) => {
            this.loanNumberField = new Field({
                ...this.loanNumberField,
                caption: val ? 'Loan Number' : '',
            });
        });

        this.form.get('zipCode')?.valueChanges.subscribe((val) => {
            this.zipCodeField = new Field({
                ...this.zipCodeField,
                caption: val ? 'Zip Code' : '',
            });
        });

        this.form.get('firstName')?.valueChanges.subscribe((val) => {
            this.firstNameField = new Field({
                ...this.firstNameField,
                caption: val ? 'First Name' : '',
            });
        });

        this.form.get('lastName')?.valueChanges.subscribe((val) => {
            this.lastNameField = new Field({
                ...this.lastNameField,
                caption: val ? 'Last Name' : '',
            });
        });

        this.form.get('trustName')?.valueChanges.subscribe((val) => {
            this.trustNameField = new Field({
                ...this.trustNameField,
                caption: val ? 'Trust / LLC Name' : '',
            });
        });
        this.form.get('insuredType')?.valueChanges.subscribe((type) => {
            if (type === 'insured') {
                this.form.patchValue({ trustName: '' }, { emitEvent: false });
            } else if (type === 'trust') {
                this.form.patchValue({ firstName: '', lastName: '' }, { emitEvent: false });
            }
        });
    }

    onCaptchaResponse(event: any) {
        this.recaptchaValid = !!event;
    }

    onSubmit() {
        const { policyNumber, loanNumber, zipCode, insuredType, firstName, lastName, trustName } = this.form.value;
        const body: any = { policyNumber, requestType: 'getPolicySnapshotForTP', sessionId: this.sessionId };

        if (loanNumber) body.loanNumber = loanNumber;
        if (zipCode) body.zipCode = zipCode;

        if (insuredType === 'insured' && firstName && lastName) {
            body.insuredType = 'insured';
            body.firstName = firstName;
            body.lastName = lastName;
        } else if (insuredType === 'trust' && trustName) {
            body.insuredType = 'trust';
            body.name = trustName;
        }

        this.isLoading = true;
        this.loadingService.showLoading();
        this.policyDetailsService.getPolicyData(body).subscribe({
            next: (data) => {
                console.log('[ThirdPartyLogin] getPolicyData success. data keys:', Object.keys(data));
                if (data?.token) {
                    this.tokenService.setToken(data.token, true);
                }
                this.store.dispatch(loadPolicyDetailsSuccess({ data }));

                // Policy term data is resolved server-side by the Orchestrator
                // (via getPolicySnapshotForTP) and returned in the response, so
                // the third-party UI no longer calls PhoneSearch directly. The
                // Orchestrator returns terms already sorted latest-first.
                const termData: { term: string; startDate: string; endDate: string }[] = data?.termData ?? [];
                this.store.dispatch(
                    PolicyActions.storePolicyTerms({ terms: termData.map((t) => t.term), termData })
                );

                const lob = (data?.lob ?? '').toUpperCase();
                console.log('[ThirdPartyLogin] Dispatching addRecentPolicy:', {
                    policyNumber: policyNumber.trim().toUpperCase(),
                    lineOfBusiness: lob,
                });
                this.store.dispatch(
                    addRecentPolicy({
                        policyNumber: policyNumber.trim().toUpperCase(),
                        lineOfBusiness: lob,
                    })
                );

                this.loadingService.hideLoading();
                this.isLoading = false;
                this.failedAttempts = 0;
                this.router.navigate(['/profile-activities/third-party'], {
                    queryParams: { sessionId: this.sessionId, policyNumber },
                });
            },
            error: (err) => {
                console.error('[ThirdPartyLogin] getPolicyData failed:', err);
                const isParsingFailure = err.message?.includes('Http failure during parsing');
                this.loadingService.hideLoading();
                this.isLoading = false;
                if (err.status === 401 || err.status === 403 || err.status === 500 || isParsingFailure) {
                    this.failedAttempts++;
                    this.failedAttemptLog.push({ ...this.form.value });
                    if (err.error?.token) this.failedAttemptToken = err.error.token;
                    if (this.failedAttempts >= 3) this.showContactModal = true;
                }
            },
        });
    }

    onContactUs() {
        const { policyNumber, loanNumber, zipCode, insuredType, firstName, lastName, trustName } = this.form.value;
        this.selectionService.setLoginFormData({
            policyNumber,
            loanNumber,
            zipCode,
            insuredType,
            firstName,
            lastName,
            trustName,
        });

        // Store the failed attempt token in session storage
        if (this.failedAttemptToken) {
            sessionStorage.setItem('token', this.failedAttemptToken);
        }

        this.router.navigate(['/contact-us'], {
            queryParamsHandling: 'preserve',
        });

        // this.isLoading = true;
        // this.loadingService.showLoading();
    }
    closeContactModal() {
        this.showContactModal = false;
        this.contactForm.reset();
    }

    onContactSubmit() {
        if (this.contactForm.valid) {
            const { contactEmail, contactPhone, contactDescription } = this.contactForm.value;
            const { policyNumber, loanNumber, zipCode, insuredType, firstName, lastName, trustName } = this.form.value;

            const payload: IrisTaskPayload = {
                policyNumber,
                email: contactEmail,
                phone: contactPhone,
                description: contactDescription,
                loanNumber,
                zipCode,
                insuredType,
                firstName,
                lastName,
                trustName,
            };

            this.createTaskIrisService.createTask(payload, this.failedAttemptToken).subscribe({
                next: () => {
                    this.showContactModal = false;
                    this.contactForm.reset();

                    this.router.navigate(['/third-party-login'], {
                        queryParams: { sessionId: this.sessionId, showSuccess: true },
                    });
                    this.toastService.add({
                        type: 'success',
                        title: '',
                        message:
                            'Thank you for reaching out. Your request has been submitted. A PSIC representative will contact you shortly.',
                    });
                },
                error: () => {
                    this.toastService.add({ type: 'error', title: '', message: 'Failed to submit request.' });
                },
            });
        }
    }

    oncancelContact() {
        this.showContactModal = false;
        this.router.navigate(['/third-party-login'], {
            queryParams: { sessionId: this.sessionId },
        });
    }
}
